'use client'

import { Button } from '@/components/ui/shadcn/button'
import Empty from '@/components/common/feedback/Empty'
import { Users, RotateCcw, Settings, Trash2 } from 'lucide-react'
import * as Icons from 'lucide-react'
import { cn } from '@/lib/utils'

export default function TeamsTab({
  teams,
  setArchiveTeam,
  setUnarchiveTeam,
  setDeleteTeam,
  setShowCreate,
  router,
  canEdit,
  canDelete,
  archived = false,
}) {
  if (teams.length < 1) {
    return (
      <div className="flex-1 flex items-center">
        <Empty
          title={archived ? 'No Archived Teams' : 'No Teams'}
          description={
            archived
              ? 'There are no archived teams. Teams can be archived from the Teams tab to hide them from normal views.'
              : "You haven't created any teams yet. Teams help organize your workspace by grouping users, resources, and permissions."
          }
          action={!archived && setShowCreate ? () => setShowCreate(true) : null}
          actionText="Create Team"
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {teams?.map(team => {
        const IconComponent =
          team.icon && Icons[team.icon] ? Icons[team.icon] : Users

        return (
          <div
            key={team.id}
            onClick={() => router.push(`/manager/settings/teams/${team.id}`)}
            className="flex cursor-pointer items-center gap-4 rounded-md border bg-card p-4 transition-colors hover:bg-muted/50"
          >
            <div
              className={cn(
                'flex size-10 shrink-0 items-center justify-center rounded-md text-white',
                archived && 'opacity-60',
              )}
              style={{ backgroundColor: team.color || 'var(--primary)' }}
            >
              <IconComponent className="size-5" />
            </div>

            <div className={cn('min-w-0 flex-1', archived && 'opacity-70')}>
              <p className="text-base font-semibold">{team.name}</p>
              {team.description && (
                <p className="text-sm text-muted-foreground">
                  {team.description}
                </p>
              )}
              <p className="mt-0.5 text-xs text-muted-foreground">
                {team.members?.length || 0} members •{' '}
                {archived ? 'Archived' : 'Created'}{' '}
                {new Date(
                  archived ? team.archivedAt : team.createdAt,
                ).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>

            <div
              className="flex shrink-0 items-center gap-1"
              onClick={e => e.stopPropagation()}
            >
              {archived ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUnarchiveTeam(team)}
                >
                  <RotateCcw className="size-4 mr-1" />
                  Unarchive
                </Button>
              ) : null}

              {!archived && canEdit && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() =>
                    router.push(`/manager/settings/teams/${team.id}/edit`)
                  }
                  aria-label="Edit team"
                >
                  <Settings className="size-4" />
                </Button>
              )}

              {canDelete && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() =>
                    archived ? setDeleteTeam(team) : setArchiveTeam(team)
                  }
                  aria-label={archived ? 'Delete permanently' : 'Archive team'}
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
